import React, {useEffect, useState} from 'react';
import clsx from 'clsx';
import {SelectField} from '@netx/core-components/SelectField';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {UserAutoList} from '@netx/core-user-next/components';
import {SearchRuleWarningLabel, SearchSubTypeLabel} from '../../../../locales';
import {CommonRuleEditorProps} from '../../../types';
import {UserType} from '../../constants';
import {UserTypeLabel} from '../../locales';
import {
	getUserRuleEditorUtilityClass,
	USER_RULE_EDITOR_CLASS_NAME,
	userRuleEditorClasses,
	UserRuleEditorClasses,
} from './userRuleEditorClasses';

export type UserRuleEditorOwnerState = WithClasses<UserRuleEditorClasses, {}>;

const useUtilityClasses = (ownerState: UserRuleEditorOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getUserRuleEditorUtilityClass, classes);
};

const UserRuleEditorRoot = styled('div', {
	name: USER_RULE_EDITOR_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	'& > * + *': {
		marginLeft: theme.spacing(1),
	},
}));

const getSubType1OptionLabel = (option: UserType) => UserTypeLabel[option];

export type UserRuleEditorProps = WithClasses<
	UserRuleEditorClasses,
	Pick<CommonRuleEditorProps, 'validationErrors'> & {
		elementSubType1: UserType;
		elementSubType1Options: UserType[];
		elementValue1: number;
		elementValue1Unavailable: boolean;
		elementValue1Label: string;
		elementValue1Loading: boolean;
		onSubType1Change: (subType1: UserType) => void;
		onValue1Change: (value: number) => void;
	}
>;

const UserRuleEditorInner = ({
	classes: _classes,
	className,
	elementSubType1,
	elementSubType1Options,
	elementValue1,
	elementValue1Unavailable,
	elementValue1Label,
	elementValue1Loading,
	validationErrors,
	onSubType1Change,
	onValue1Change,
}: UserRuleEditorProps) => {
	const ownerState: UserRuleEditorOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const [userName, setUserName] = useState('');

	useEffect(() => {
		setUserName(elementValue1Label);
	}, [elementValue1Label]);

	const waitingForUserLabel = elementValue1Loading && userName === '';

	return (
		<UserRuleEditorRoot className={clsx(classes.root, className)}>
			<SelectField<UserType, false, true, false>
				data-testid="user-rule-sub-type-1"
				disableClearable
				label={SearchSubTypeLabel.MATCH_TYPE}
				name={'rule-sub-type-1'}
				options={elementSubType1Options}
				value={elementSubType1}
				getOptionLabel={getSubType1OptionLabel}
				onChange={(event, value) => onSubType1Change(value)}
			/>

			<UserAutoList<false, true>
				data-testid="user-rule-value-1"
				disableClearable
				error={validationErrors?.elementValue1?.message}
				loadingValue={elementValue1Loading}
				name={'rule-value-1'}
				placeholder={
					waitingForUserLabel
						? 'loading...'
						: elementValue1Unavailable
							? SearchRuleWarningLabel.DATA_UNAVAILABLE
							: undefined
				}
				value={
					elementValue1Unavailable || waitingForUserLabel
						? (null as any)
						: {
								name: userName,
								id: elementValue1,
							}
				}
				onChange={(event, user) => {
					setUserName(user.name);
					onValue1Change(user.id);
				}}
				InputProps={{
					InputLabelProps: {
						shrink: elementValue1Unavailable || undefined,
					},
				}}
			/>
		</UserRuleEditorRoot>
	);
};

export const UserRuleEditorPrimitive = UserRuleEditorInner;

export const UserRuleEditor = UserRuleEditorPrimitive;

export default UserRuleEditor;

export type UserRuleEditorGenericProps = WithClasses<
	UserRuleEditorClasses,
	CommonRuleEditorProps & {}
>;

export const UserRuleEditorGenericPrimitive = ({
	elementValue1,
	...props
}: UserRuleEditorGenericProps) => {
	return (
		<UserRuleEditor
			{...props}
			elementValue1={parseInt(elementValue1.toString()) || 0}
		/>
	);
};

export const UserRuleEditorGeneric = UserRuleEditorGenericPrimitive;
